import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { twMerge } from 'tailwind-merge';
import './tooltip.css';

interface SimpleTooltipProps {
  content: ReactNode;
  children: ReactNode;
  wrapperBackgroundColor?: string;
  wrapperBorderColor?: string;
  wrapperClassName?: string;
}

function TooltipV2({
  content,
  children,
  wrapperBackgroundColor,
  wrapperBorderColor,
  wrapperClassName,
}: SimpleTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 12],
        },
      },
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 0,
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'left', 'right'],
        },
      },
    ],
  });

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  return (
    <>
      <div
        ref={setReferenceElement}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </div>

      {isVisible &&
        createPortal(
          <div
            ref={setPopperElement}
            style={{
              ...styles.popper,
              backgroundColor: wrapperBackgroundColor,
              borderColor: wrapperBorderColor,
            }}
            {...attributes.popper}
            className={twMerge(
              'relative z-50 rounded-lg border px-3 py-2 text-sm text-white shadow-lg',
              wrapperClassName,
            )}
            role="tooltip"
          >
            {content}
            <div
              data-popper-arrow
              ref={setArrowElement}
              style={{
                ...styles.arrow,
                backgroundColor: wrapperBackgroundColor,
                borderColor: wrapperBorderColor,
              }}
              className={twMerge(
                'tooltip-arrow absolute h-3 w-3 rotate-45',
                wrapperBackgroundColor,
                wrapperBorderColor,
              )}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

export default TooltipV2;
